import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { newsAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { RichTextContent } from '../ui/rich-text-content';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import { SEO } from '../seo/SEO';

export const NewsDetailPage = ({ siteKey }) => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadNews = async () => {
      try {
        const response = await newsAPI.getOne(id);
        setNews(response.data);
      } catch (error) {
        console.error('Error loading news:', error);
      } finally {
        setLoading(false);
      }
    };
    loadNews();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  if (!news) {
    return (
      <div className="py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-[#2D3748]">Aktualita nenalezena</h1>
          <Link to={`/${siteKey}/aktuality`} className="text-[#45AAF2] mt-4 inline-block">
            Zpet na aktuality
          </Link>
        </div>
      </div>
    );
  }

  // Vyextrahuj holý text z HTML pro meta description (max ~160 znaků)
  const stripHtml = (html) => {
    if (!html) return '';
    const div = typeof document !== 'undefined' ? document.createElement('div') : null;
    if (!div) return html.replace(/<[^>]*>/g, '').trim().slice(0, 160);
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim().slice(0, 160);
  };

  return (
    <div className="py-12">
      <SEO
        siteKey={siteKey}
        title={news.title}
        description={stripHtml(news.content) || `Aktualita z ${siteKey === 'ms' ? 'MŠ Cestička' : 'dětské skupiny Chaloupka na vršku'}.`}
        image={news.image_url}
        type="article"
      />
      <article className="max-w-3xl mx-auto px-4 sm:px-6">
        <Link to={`/${siteKey}/aktuality`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-6" data-testid="back-link">
          <ArrowLeft size={18} />
          Zpet na aktuality
        </Link>

        <header className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-[#2D3748] mb-4" data-testid="news-title">
            {news.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-[#64748B]">
            <div className="flex items-center gap-2">
              <Calendar size={16} />
              {formatDate(news.created_at)}
            </div>
            <div className="flex items-center gap-2">
              <User size={16} />
              {news.author_name}
            </div>
          </div>
        </header>

        {news.image_url && (
          <div className="mb-8 rounded-2xl overflow-hidden">
            <img src={news.image_url} alt={news.title} className="w-full h-auto object-cover" />
          </div>
        )}

        <div data-testid="news-content">
          <RichTextContent html={news.content} />
        </div>
      </article>
    </div>
  );
};

export default NewsDetailPage;
